import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import * as R from 'ramda';
import { useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import LoadingPage from './components/LoadingPage/LoadingPage';
import { MediaApplicationContextProvider } from './components/MediaApplicationContext';
import ModalRoot from './components/Modal/ModalRoot';
import TeceoSnackbar from './components/Snackbar/TeceoSnackbar';
import { useClaritySession } from './hooks/useClaritySession';
import { useHandleWhenWaitedTooMuchFirstLoading } from './hooks/useHandleWhenWaitedTooMuchFirstLoading';
import { useIsLoadingApp } from './hooks/useIsLoadingApp';
import { usePageTitleManager } from './hooks/usePageTitleManager';
import { PageTitleContextProvider } from './modules/context/PageTitle.context';
import ErrorBoundaryFallbackPage from './modules/errors/pages/ErrorBoundaryFallbackPage/ErrorBoundaryFallbackPage';
import { AuthContext, AuthContextProvider } from './modules/login/AuthContext';
import {
  NetworkContextProvider,
  useNetworkContext,
} from './modules/offline/contexts/network/network.context';
import CustomQueryClientProvider from './modules/reactQuery/contexts/QueryClient/QueryClient.context';
import ResetAppPage from './modules/resetApp/pages/ResetAppPage';
import { IGetSharedEntityResponse } from './modules/shared-entities/types/sharedEntities.types';
import {
  TranslationContextProvider,
  useTranslation,
} from './modules/translation/TranslationContext';
import { AbilityContextProvider } from './modules/users/AbilityContext';
import PrivateRoute from './routes/components/PrivateRoute';
import PublicRoute from './routes/components/PublicRoute';
import HomeNavigation from './routes/navigations/HomeNavigation';
import OfflineHomeNavigation from './routes/navigations/OfflineHomeNavigation/OfflineHomeNavigation';
import getPublicRoutes from './routes/public.routes';
import { useRootStyles } from './routes/routes-page.styles';
import useSharedEntitystore from './stores/SharedEntities/sharedEntities.store';
import { localeToDateFns, localeToPickersLocaleText } from './utils/dates';

const App = () => {
  'use memo';

  const rootStyles = useRootStyles();

  const { locale } = useTranslation();
  const { isOnline, isForcingOfflineMode } = useNetworkContext();
  const handleChangeSharedEntity = useSharedEntitystore((state) => state.handleChangeSharedEntity);

  const isLoadingApp = useIsLoadingApp();

  useHandleWhenWaitedTooMuchFirstLoading(isLoadingApp);
  usePageTitleManager({});
  useClaritySession();

  useEffect(() => {
    const sharedEntity = localStorage.getItem('publicSharedEntity');

    if (!R.isNil(sharedEntity)) {
      handleChangeSharedEntity(JSON.parse(sharedEntity) as IGetSharedEntityResponse);
    }
  }, [handleChangeSharedEntity]);

  return isLoadingApp ? (
    <LoadingPage />
  ) : (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={localeToDateFns[locale]}
      localeText={localeToPickersLocaleText[locale]}
    >
      <AbilityContextProvider>
        <AuthContextProvider>
          <ModalRoot />
          <AuthContext.Consumer>
            {({ loading, loggedin, loginContext }) => {
              if (loading) {
                return <LoadingPage />;
              }

              return (
                <Switch>
                  {getPublicRoutes({
                    loggedin,
                    isOnline,
                    rootStyles,
                    loginContext,
                    isForcingOfflineMode,
                  }).map(({ key, usePublicRouteComponent, ...params }) =>
                    usePublicRouteComponent ? (
                      <PublicRoute key={key} {...params} />
                    ) : (
                      <Route key={key} {...params} />
                    ),
                  )}

                  <PrivateRoute
                    path="/"
                    component={isForcingOfflineMode ? OfflineHomeNavigation : HomeNavigation}
                  />
                </Switch>
              );
            }}
          </AuthContext.Consumer>
        </AuthContextProvider>
      </AbilityContextProvider>
      <TeceoSnackbar />
    </LocalizationProvider>
  );
};

const AppContext = () => (
  <Router>
    <NetworkContextProvider>
      <CustomQueryClientProvider>
        <PageTitleContextProvider>
          <TranslationContextProvider>
            <ErrorBoundary FallbackComponent={ErrorBoundaryFallbackPage}>
              <MediaApplicationContextProvider>
                <App />
                <Route exact path="/reset-app" component={ResetAppPage} />
              </MediaApplicationContextProvider>
            </ErrorBoundary>
          </TranslationContextProvider>
        </PageTitleContextProvider>
      </CustomQueryClientProvider>
    </NetworkContextProvider>
  </Router>
);

export default AppContext;
